import {
  isPrimitive,
  isPrimitiveArray,
  isPrimitiveMap,
  isPrimitiveRecordArray,
} from '@smartsheet-dataflow/formjs';
import compose from 'lodash/fp/compose';
import concat from 'lodash/fp/concat';
import { Dispatch } from 'redux';
import { BridgeFormJSState } from '../Types';
import {
  reduceArrayItemsLabel,
  reduceIsOpen,
  reduceLink,
  reduceSchema,
} from '../features';
import {
  getParameterId,
  getParameterType,
  isGroupArrayModel,
  isGroupModel,
  isGroupParamType,
  isHTMLLinkModel,
  isHTMLLinkType,
  isMapParamType,
  isPrimitiveParamType,
} from '../utils';
import { AllParamModel } from './ParamModelTypes';
import {
  GroupParamSchema,
  ParamSchema,
  ParamSchemaTypes,
} from './ParamSchemaTypes';
import { reduceChildren } from './children/ChildrenFromSchema';
import { reduceComponent } from './component/ComponentFromSchema';
import { reduceHidden } from './hidden/HiddenFromSchema';
import { reduceLabel } from './label/LabelFromSchema';
import { reduceReadonly } from './readonly/ReadonlyFromSchema';
import { reduceSelect } from './select/SelectFromSchema';
import { reduceTitle } from './title/TitleFromSchema';
import { reduceValue } from './value/ValueFromSchema';
import { getValueParamsFromSchema } from './valueparams/ValueParamsDecoder';

export const dummyDispatch: Dispatch = a => a;

export const getParamFromSchema =
  (parentId: string, model: AllParamModel | undefined) =>
  (state: BridgeFormJSState, param: ParamSchema): BridgeFormJSState => {
    const inputId = getParameterId(param, parentId);
    const [reducer, ...reducers] = [
      reduceComponent({
        inputId,
        component: getParameterType(param),
      }),
      reduceChildren({
        inputId: parentId,
        children: concat(state.children[parentId] || [], [inputId]),
      }),
      reduceLabel({
        inputId,
        label: param.displayName || '',
      }),
      reduceTitle({
        inputId,
        title: param.description || '',
      }),
      reduceReadonly({
        inputId,
        readonly: param.readOnly !== undefined ? param.readOnly : false,
      }),
    ];

    if (param.hide === true) {
      reducers.push(reduceHidden({ inputId, hidden: true }));
    } else if (
      param.hideExpression !== undefined &&
      param.hideExpression.length > 0
    ) {
      const hideExpression = param.hideExpression;
      reducers.push((s: BridgeFormJSState): BridgeFormJSState => {
        return {
          ...s,
          hideExpression: {
            ...s.hideExpression,
            [`${inputId}.hidden`]: hideExpression.replace(
              /(?:^|\s)model\./g,
              `${parentId}.`
            ),
          },
        };
      });
    }

    if (param.type === 'ENUM') {
      reducers.push(reduceSelect(param, parentId));
    }

    const childModel = getModelFromInputId(param, model);

    if (param.type === 'PARAM') {
      if (param.allowArray) {
        const arrayReducer = createArrayReducer(param, inputId, childModel);
        reducers.push(
          reduceSchema({
            inputId,
            schema: param,
          })
        );
        if (arrayReducer !== undefined) {
          reducers.push(arrayReducer);
        }
      } else {
        reducers.push(getValueParamsFromSchema(param, inputId, childModel));
      }
    } else if (isHTMLLinkType(param.type) && isHTMLLinkModel(childModel)) {
      reducers.push(
        reduceLink({
          inputId,
          link: childModel.value,
        })
      );
      reducers.push(
        reduceValue({
          inputId,
          value: childModel.label,
        })
      );
    } else if (childModel !== undefined) {
      const valueReducer = createValueReducer(inputId, childModel);
      if (valueReducer !== undefined) {
        reducers.unshift(valueReducer);
      }
    }

    if (
      param.type === ParamSchemaTypes.PARAM ||
      param.type === ParamSchemaTypes.MAP ||
      (param.type === ParamSchemaTypes.STRING && param.allowArray === true)
    ) {
      reducers.push(
        reduceIsOpen({
          inputId,
          isOpen: param.isOpen || false,
        })
      );
    }

    if (
      param.allowArray === true &&
      (param.type === ParamSchemaTypes.PARAM ||
        param.type === ParamSchemaTypes.STRING)
    ) {
      reducers.push(
        reduceArrayItemsLabel({
          inputId,
          arrayItemsLabel: param.arrayItemsLabel || '',
        })
      );
    }

    return compose(reducer, ...reducers)(state);
  };

const getModelFromInputId = (
  param: ParamSchema,
  model: AllParamModel | undefined
): AllParamModel | undefined => {
  if (!isGroupModel(model)) {
    return model;
  }

  if (
    isPrimitiveParamType(param.type) &&
    (isPrimitive(model[param.param]) || isPrimitiveArray(model[param.param]))
  ) {
    return model[param.param];
  }

  if (isMapParamType(param.type) && isPrimitiveMap(model[param.param])) {
    return Object.entries(model[param.param]).map(([key, value]) => ({
      key,
      value,
    }));
  }

  if (isGroupParamType(param.type) && isGroupModel(model)) {
    if (
      isGroupModel(model[param.param]) ||
      isGroupArrayModel(model[param.param])
    ) {
      return model[param.param];
    }
    return model;
  }

  if (
    isHTMLLinkType(param.type) &&
    (isHTMLLinkModel(model[param.param]) || isPrimitive(model[param.param]))
  ) {
    return model[param.param];
  }
};

const createValueReducer = (inputId: string, model: AllParamModel) => {
  if (
    isPrimitive(model) ||
    isPrimitiveArray(model) ||
    isPrimitiveRecordArray(model)
  ) {
    return reduceValue({
      inputId,
      value: model,
    });
  }
};

const createArrayReducer = (
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  { param, allowArray, ...rest }: GroupParamSchema,
  inputId: string,
  model: AllParamModel | undefined
) => {
  if (isGroupArrayModel(model)) {
    return (state: BridgeFormJSState) => {
      return model.reduce<BridgeFormJSState>((acc, value, index) => {
        return getParamFromSchema(inputId, value)(acc, {
          param: `item${index}`,
          allowArray: false,
          ...rest,
        });
      }, state);
    };
  }
};
